import { useRef, useState, useEffect } from "react";

import { Box, Typography, CircularProgress } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { getSurveyVersionCompare } from "@rubi/api-generated";
import type {
  ComputedDiff,
  DiffLine,
} from "@rubi/api-generated/clientRs/types.gen";
import { useParams } from "react-router-dom";

import { formatVersionNumber } from "./utils";

// Minimal version interface for what this component actually needs
interface VersionForComparison {
  id: string;
  parent_survey_id: string;
  major_version?: number | null;
  minor_version?: number | null;
  configuration: { [key: string]: unknown };
  pdf_configuration: { [key: string]: unknown };
}

interface VersionComparisonProps {
  fromVersion: VersionForComparison;
  toVersion: VersionForComparison;
  viewType: "configuration" | "pdf_configuration";
  // Optional pre-computed diffs from backend (e.g., from bulk edit preview)
  configurationDiff?: ComputedDiff;
  pdfConfigurationDiff?: ComputedDiff;
}

// This component now uses diffs computed by the backend using the Myers algorithm
// which properly handles moved content and ignores JSON formatting artifacts like trailing commas

export const VersionComparison = ({
  fromVersion,
  toVersion,
  viewType,
  configurationDiff,
  pdfConfigurationDiff,
}: VersionComparisonProps) => {
  const params = useParams();
  const orgId = params.orgId;
  const parentSurveyId = fromVersion.parent_survey_id;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollPercentage, setScrollPercentage] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [linePositions, setLinePositions] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchedConfigDiff, setFetchedConfigDiff] =
    useState<ComputedDiff | null>(null);
  const [fetchedPdfDiff, setFetchedPdfDiff] = useState<ComputedDiff | null>(
    null,
  );

  // If diffs are not provided, fetch them from the API
  useEffect(() => {
    if (configurationDiff && pdfConfigurationDiff) {
      // Diffs already provided (from bulk edit preview)
      return;
    }

    const fetchDiff = async () => {
      if (!orgId || !parentSurveyId || !fromVersion.id || !toVersion.id) {
        return;
      }

      setLoading(true);
      try {
        const response = await getSurveyVersionCompare({
          path: {
            org_id: orgId,
            parent_survey_id: parentSurveyId,
          },
          query: {
            from_version_id: fromVersion.id,
            to_version_id: toVersion.id,
          },
        });

        if (response.data) {
          setFetchedConfigDiff(
            response.data.configuration_diff as ComputedDiff,
          );
          setFetchedPdfDiff(
            response.data.pdf_configuration_diff as ComputedDiff,
          );
        }
      } catch (error) {
        console.error("Failed to fetch diff:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDiff();
  }, [
    fromVersion.id,
    toVersion.id,
    orgId,
    parentSurveyId,
    configurationDiff,
    pdfConfigurationDiff,
  ]);

  // Use provided diff or fetched diff
  const configDiff = configurationDiff || fetchedConfigDiff;
  const pdfDiff = pdfConfigurationDiff || fetchedPdfDiff;
  const diff = viewType === "configuration" ? configDiff : pdfDiff;

  const leftLines = diff?.left_lines || [];
  const rightLines = diff?.right_lines || [];

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const calculateLinePositions = () => {
      const lineElements =
        container.querySelectorAll<HTMLElement>("[data-line-index]");
      const scrollHeight = container.scrollHeight;
      const positions: number[] = [];

      lineElements.forEach((element) => {
        const relativeTop = element.offsetTop;
        const percentage = (relativeTop / scrollHeight) * 100;
        positions.push(percentage);
      });

      setLinePositions(positions);
    };

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      if (scrollHeight > clientHeight) {
        const percentage = scrollTop / (scrollHeight - clientHeight);
        setScrollPercentage(percentage);
        setViewportHeight((clientHeight / scrollHeight) * 100);
      } else {
        setScrollPercentage(0);
        setViewportHeight(100);
      }
    };

    // Calculate positions after render
    setTimeout(calculateLinePositions, 100);

    handleScroll(); // Initial call
    container.addEventListener("scroll", handleScroll);

    const resizeObserver = new ResizeObserver(() => {
      handleScroll();
      calculateLinePositions();
    });
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [leftLines]);

  const handleMinimapClick = (clickY: number, containerHeight: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const percentage = clickY / containerHeight;
    const scrollHeight = container.scrollHeight;
    const targetScrollTop =
      percentage * scrollHeight - container.clientHeight / 2;

    container.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: "smooth",
    });
  };

  const getLineStyle = (type: DiffLine["type"]) => {
    switch (type) {
      case "added":
        return { bgcolor: "action.hover", color: "text.primary" };
      case "removed":
        return { bgcolor: "action.selected", color: "text.primary" };
      case "blank":
        return { bgcolor: "action.hover", color: "transparent" };
      case "separator":
        return { bgcolor: "action.hover", color: "action.active" };
      default:
        return { bgcolor: "background.paper", color: "text.primary" };
    }
  };

  const fromData = fromVersion[viewType];
  const toData = toVersion[viewType];
  const isFromEmpty = !fromData || Object.keys(fromData).length === 0;
  const isToEmpty = !toData || Object.keys(toData).length === 0;
  // Skip bothEmpty check if pre-computed diffs are provided
  const hasPrecomputedDiffs = configurationDiff || pdfConfigurationDiff;
  const bothEmpty = !hasPrecomputedDiffs && isFromEmpty && isToEmpty;

  if (loading || !diff) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {loading ? (
          <CircularProgress />
        ) : (
          <Typography variant="body1" color="text.secondary">
            No comparison available
          </Typography>
        )}
      </Box>
    );
  }

  if (bothEmpty) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No {viewType === "configuration" ? "main" : "condensed"} report
          configuration found in either version
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        position: "relative",
      }}
    >
      {/* Main diff content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {/* Headers */}
        <Box
          sx={{
            display: "flex",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              flex: 1,
              p: 1.5,
              bgcolor: "action.hover",
              borderRight: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold">
              Version {formatVersionNumber(fromVersion)}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              p: 1.5,
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold">
              Version {formatVersionNumber(toVersion)}
            </Typography>
          </Box>
        </Box>

        {/* Scrollable content area */}
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              fontFamily: "monospace",
              fontSize: "12px",
              lineHeight: "20px",
            }}
          >
            {leftLines.map((leftLine, index) => {
              const rightLine = rightLines[index];

              // Handle separator rows - render as a single centered row
              if (leftLine.type === "separator") {
                return (
                  <Box
                    key={index}
                    data-line-index={index}
                    sx={{
                      display: "flex",
                      minHeight: "24px",
                      bgcolor: "action.hover",
                      borderTop: "1px solid",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontStyle: "italic",
                        userSelect: "none",
                      }}
                    >
                      ··· {leftLine.collapsed_count} unchanged lines ···
                    </Typography>
                  </Box>
                );
              }

              // Regular diff line rendering
              return (
                <Box
                  key={index}
                  data-line-index={index}
                  sx={{
                    display: "flex",
                    minHeight: "20px",
                  }}
                >
                  {/* Left side */}
                  <Box
                    sx={{
                      flex: 1,
                      ...getLineStyle(leftLine.type),
                      px: 1,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                      display: "flex",
                      borderRight: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        color:
                          leftLine.type === "removed"
                            ? "error.main"
                            : "text.secondary",
                        minWidth: "40px",
                        flexShrink: 0,
                        userSelect: "none",
                        textAlign: "right",
                        pr: 1,
                      }}
                    >
                      {leftLine.line_number ?? leftLine.marker}
                    </Box>
                    <Box
                      component="span"
                      sx={{ flex: 1, overflowWrap: "break-word" }}
                    >
                      {leftLine.content}
                    </Box>
                  </Box>

                  {/* Right side */}
                  <Box
                    sx={{
                      flex: 1,
                      ...getLineStyle(rightLine.type),
                      px: 1,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                      display: "flex",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        color:
                          rightLine.type === "added"
                            ? "success.main"
                            : "text.secondary",
                        minWidth: "40px",
                        flexShrink: 0,
                        userSelect: "none",
                        textAlign: "right",
                        pr: 1,
                      }}
                    >
                      {rightLine.line_number ?? rightLine.marker}
                    </Box>
                    <Box
                      component="span"
                      sx={{ flex: 1, overflowWrap: "break-word" }}
                    >
                      {rightLine.content}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Minimap */}
      <Box
        sx={{
          width: "50px",
          bgcolor: "action.hover",
          borderLeft: "1px solid",
          borderColor: "divider",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            p: 1.5,
            flexShrink: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "action.hover",
          }}
        >
          <Typography variant="subtitle2" sx={{ opacity: 0 }}>
            &nbsp;
          </Typography>
        </Box>
        <Box
          sx={{ flex: 1, position: "relative", overflow: "hidden" }}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const clickY = e.clientY - rect.top;
            handleMinimapClick(clickY, rect.height);
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              p: 0.5,
              cursor: "pointer",
            }}
          >
            {leftLines.map((leftLine, index) => {
              const rightLine = rightLines[index];

              let bgColor: string = "divider";
              let isChange = false;

              // Handle separator lines - show as subtle gray
              if (leftLine.type === "separator") {
                bgColor = "action.disabled";
                isChange = false;
              } else if (rightLine.type === "added") {
                bgColor = "success.light";
                isChange = true;
              } else if (leftLine.type === "removed") {
                bgColor = "error.main";
                isChange = true;
              } else if (
                leftLine.type === "unchanged" &&
                rightLine.type === "unchanged"
              ) {
                isChange = false;
              }

              const topPosition =
                linePositions[index] || (index / leftLines.length) * 100;
              const nextPosition =
                linePositions[index + 1] ||
                ((index + 1) / leftLines.length) * 100;
              const height = nextPosition - topPosition;

              return (
                <Box
                  key={index}
                  sx={{
                    position: "absolute",
                    top: `${topPosition}%`,
                    left: 0,
                    right: 0,
                    height: `${height}%`,
                    bgcolor: bgColor,
                    opacity: isChange ? 1 : 0.3,
                    "&:hover": {
                      opacity: 1,
                    },
                  }}
                />
              );
            })}
          </Box>

          {/* Viewport indicator */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: `${scrollPercentage * (100 - viewportHeight)}%`,
              height: `${viewportHeight}%`,
              bgcolor: (theme) => alpha(theme.palette.text.primary, 0.15),
              border: "1px solid",
              borderColor: (theme) => alpha(theme.palette.text.primary, 0.3),
              pointerEvents: "none",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
